import * as React from 'react'
import { createPortal } from 'react-dom'

import YearPicker from '../date/YearPicker'
import { faCalendarSolid, FaIcon } from '@/lib/fa'

type YearPickerButtonProps = {
  value?: string | number | null
  handleChangeValue?: (year: string | number | null) => void
  defaultValue?: number | null
}

export const YearPickerButton = ({
  value,
  handleChangeValue,
  defaultValue,
}: YearPickerButtonProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [open, setOpen] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })
  const isControlled = value !== undefined
  const [internalYear, setInternalYear] = React.useState<
    string | number | null
  >(defaultValue || null)
  const year = isControlled ? value : internalYear

  const setYear = (newYear: number | string) => {
    if (isControlled) {
      handleChangeValue?.(newYear)
    } else {
      setInternalYear(newYear)
    }
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      })
    }
    setOpen(willOpen)
  }

  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current || !dropdownRef.current) return

    const margin = 8
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = dropdownRef.current.offsetWidth
    const dropdownHeight = dropdownRef.current.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.left
    if (left + dropdownWidth > viewportWidth - margin) {
      left = rect.right - dropdownWidth
    }
    if (left < margin) {
      left = margin
    }

    let top = rect.bottom + 4
    if (top + dropdownHeight > viewportHeight - margin) {
      const above = rect.top - dropdownHeight - 4
      if (above > margin) {
        top = above
      }
    }

    setDropdownPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    )
  }, [open])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-neutral/20 tw-bg-transparent tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${
          open && 'tw-border-2 tw-border-primary'
        }`}
      >
        <FaIcon
          icon={faCalendarSolid}
          className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
        />
        <p className="tw-text-sm">{year || 'Wybierz rok'}</p>
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[200] tw-shadow-md"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <YearPicker
              value={year ? year.toString() : undefined}
              onConfirm={(newYear) => {
                setYear(Number(newYear))
                setOpen(false)
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default YearPickerButton
